import { FormEvent, useEffect, useState } from 'react';

import { CodeAPI } from 'api/codeAPI';
import { Form, Formik } from 'formik';

import { OutlinedButton, SubmitButton, Input } from 'components/UIComponents';
import { StyledDialogActions as DialogActions } from 'components/base/BaseModal/styledDialogActions';
import { StyledDialogContent as DialogContent } from 'components/base/BaseModal/styledDialogContent';

import { validationEditCode } from 'helpers/validation/codeValidationSchema';

import { ICodeEditForm } from 'interfaces/Code/ICodeEditForm';
import { IIsciCode } from 'interfaces/IsciCode/IIsciCode';

interface EditCodeFormProps {
  creativeId: number;
  onModalClose: () => void;
  successAction: () => void;
  editCodeId: number;
  currentCreativeType: string;
}

const MAX_ISCI_CODES_NUMBER = 3;

const initializedIsciCodes = (currentCreativeType: string, isciCodes: IIsciCode[]): string[] => {
  if (currentCreativeType === 'Live') {
    return isciCodes.map((code: IIsciCode) => code.isci || '');
  } else {
    const result = Array(MAX_ISCI_CODES_NUMBER).fill('');
    isciCodes.forEach((isciCode: IIsciCode) => result[isciCode.index] = isciCode.isci);
    return result;
  }
};

const EditCodeForm = ({
  creativeId, onModalClose, successAction, editCodeId, currentCreativeType,
}: EditCodeFormProps) => {
  const [codeFormInformation, setCodeFormInformation] = useState<ICodeEditForm>({
    code: '',
    isci_codes: Array(MAX_ISCI_CODES_NUMBER).fill(''),
    keyword: '',
  });

  useEffect(() => {
    if (editCodeId) {
      CodeAPI.getOne(creativeId, editCodeId).then(({ data }) => {
        setCodeFormInformation({
          code: data.code,
          isci_codes: initializedIsciCodes(currentCreativeType, data.isci_codes),
          keyword: data.keyword || '',
        });
      });
    }
  }, [editCodeId, creativeId, currentCreativeType]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  return (
    <>
      <Formik
        initialValues={codeFormInformation}
        enableReinitialize={true}
        validationSchema={validationEditCode}
        validateOnBlur
        onSubmit={(values) => {
          setIsDisabled(true);
          CodeAPI.updateCode(creativeId, editCodeId, values).then(() => {
            onModalClose();
            successAction();
            setIsDisabled(false);
          });
        }}
      >
        {({ errors, handleSubmit, dirty }) => (
          <Form>
            <DialogContent>
              <Input name="code" label="Code" required />
              { currentCreativeType === 'Live' && <Input name="isci_codes[0]" label="ISCI" required /> }
              <Input name="keyword" label="Keyword" />
              { currentCreativeType === 'Spot' &&
                <>
                  <Input name="isci_codes[0]" label="ISCI #1" required />
                  <Input name="isci_codes[1]" label="ISCI #2" />
                  <Input name="isci_codes[2]" label="ISCI #3" />
                </>
              }
            </DialogContent>
            <DialogActions>
              <OutlinedButton onClick={onModalClose}>Cancel</OutlinedButton>
              <SubmitButton
                disabled={isDisabled || !dirty || !!Object.values(errors).length}
                onClick={(values: FormEvent<HTMLFormElement>) => handleSubmit(values)}
              >
                Save
              </SubmitButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EditCodeForm;
